import VueRouter from "vue-router";
import VuexRouterSync from "vuex-router-sync";
import routes from "./routes";
import store from "@/store";

import accountService from "@/services/account";

import { routerPermissionsSetup } from "@/plugins/accountPermissions";

const ERROR_403_PAGE_NAME = "Access Denied";

const checkPermissions = (to, from, next) => {
  const userCan = store.getters["account/userCan"];
  const banned = to.matched
    .map(({ meta: { ifCan } }) => ifCan)
    .filter((ifCan) => ifCan)
    .filter((ifCan) =>
      Object.entries(ifCan).some(
        ([permission, resource]) => !userCan(permission, resource)
      )
    );

  if (banned.length) {
    const params = { description: "meh" };
    const permString = banned
      .map((ban) =>
        Object.entries(ban)
          .map(([perm, res]) => `${perm}: ${res}`)
          .join(", ")
      )
      .join("\n ");
    if (to.name !== ERROR_403_PAGE_NAME) {
      return next({ name: ERROR_403_PAGE_NAME, query: { to: to.fullPath } });
    } else {
      next(false);
    }
  }

  return next();
};

let compatRoutes;

compatRoutes = (routes) =>
  routes
    .map(({ meta, ...route }) => ({ meta: meta || {}, ...route }))
    .map(({ ifCan, meta, children, ...route }) => ({
      meta: {
        ...meta,
        ...(ifCan ? { ifCan } : {}),
      },
      ...(children ? { children: compatRoutes(children) } : {}),
      ...route,
    }));

export default {
  install(Vue) {
    // Router settings
    const router = new VueRouter({
      mode: "history",
      routes: compatRoutes(routes),
      scrollBehavior(to, from, savedPosition) {
        // return desired position
        return { x: 0, y: 0 };
      },
    });

    // Setting RouterSync
    VuexRouterSync.sync(store, router);
    Vue.router = router;

    router.beforeEach((to, from, next) => {
      if (to.name) {
        store.dispatch("setLoadingPage", true);
      }
      next();
    });

    router.afterEach((to, from) => {
      store.dispatch("setLoadingPage", false);
    });

    router.beforeEach((to, from, next) => {
      store.dispatch("auth/check");
      const authenticated = store.state.auth.authenticated;

      if (!authenticated) {
        if (to.fullPath === "/login" || to.name === "Login") {
          next();
        } else {
          next({
            name: "Login",
            query: { nextURL: to.fullPath },
          });
        }

        return;
      }

      const { userId } = store.state.account;

      let checkAuth;
      if (!userId) {
        checkAuth = accountService.find();
      } else {
        checkAuth = Promise.resolve();
      }

      checkAuth
        .then(() => {
          if (to.fullPath === "/login" || to.name === "Login") {
            next({ name: "Dashboard" });
          } else {
            const routeName = (to.meta && to.meta.title) || to.name;
            document.title = `${Vue.config.appName} - ${routeName}`;
            next();
          }
        })
        .catch((err) => {
          // A 401 was already logged out by the Axios interceptor; only keep
          // the target as nextURL (the interceptor uses the current route).
          if (!store.state.auth.authenticated) {
            const query = to.name !== "Login" ? { nextURL: to.fullPath } : {};
            router.replace({ name: "Login", query });
            return;
          }
          // Any other failure (5xx, network) is not an auth problem: logging
          // out would clear the token every tab shares, and the next login
          // would hit TOKEN_LIMIT_PER_USER and close the user's other
          // sessions. Stay logged in and retry instead.
          console.error(err);
          if (!from.matched.length) {
            // First navigation of the tab: there is no layout to show an
            // error in, and next(false) would drop the URL. Retry until the
            // backend answers (e.g. during a deploy).
            setTimeout(() => router.replace(to.fullPath), 5000);
            return;
          }
          next(false);
          router.app.$notify({
            message: "No se pudo cargar tu sesión. Intenta de nuevo.",
            icon: "add_alert",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger",
          });
        });
    });

    router.beforeEach((to, from, next) => {
      const userRole = store.getters["account/userRole"];
      const banned = to.matched.some(({ meta }) => {
        const { roles, excludedRoles } = meta || {};
        return (
          (roles && !roles.includes(userRole)) ||
          (excludedRoles && excludedRoles.includes(userRole))
        );
      });

      if (banned) {
        const params = { description: "meh", from: to.fullPath };
        if (from.name !== ERROR_403_PAGE_NAME) {
          next({ name: ERROR_403_PAGE_NAME, query: params });
        } else {
          return next();
        }
      }

      next();
    });

    router.beforeEach(checkPermissions);

    Vue.use(VueRouter);
    Vue.use(VuexRouterSync);

    routerPermissionsSetup(router);

    return router;
  },
};



// WEBPACK FOOTER //
// ./src/router/index.js